import clsx from 'clsx';
import React, { useCallback } from 'react';
import { CSSTransition } from 'react-transition-group';
import { arrow, autoUpdate, flip, FloatingPortal, offset, shift, useFloating, useId, } from '@floating-ui/react';
import { useControlled } from '@cmpkit/hooks';
import { composeRefs, getArrowSize } from '../utils';
function _BaseFloatingElement({ children, content, root, placement = 'top', isOpen, hidePointer = true, role, inline, useInteractions, path, onDismiss, className, buildRefElAriaAttributes, buildFloatingElAriaAttributes, fallbackBehaviour = ['flip', 'shift'], boundary, }, ref) {
    var _a, _b;
    const [open, setOpen] = useControlled({
        controlledValue: isOpen,
        defaultValue: false,
    });
    const distance = 10;
    const pointerPadding = 10;
    const panelRef = React.useRef(null);
    const pointerRef = React.useRef(null);
    const { x, y, refs, strategy, context, middlewareData: { arrow: { x: pointerX, y: pointerY } = {} }, placement: statefulPlacement, } = useFloating({
        placement,
        open,
        onOpenChange: (isOpen) => {
            if (!isOpen && onDismiss) {
                onDismiss();
            }
            setOpen(isOpen);
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            ...(distance ? [offset(distance)] : []),
            ...(fallbackBehaviour.includes('flip') ? [flip({ boundary })] : []),
            ...(fallbackBehaviour.includes('shift') ? [shift({ boundary })] : []),
            ...(!hidePointer
                ? [arrow({ element: pointerRef, padding: pointerPadding })]
                : []),
        ],
    });
    const triggerRef = useCallback(composeRefs(refs.setReference, children.ref), [
        refs.setReference,
        children.ref,
    ]);
    const defaultRefId = `ref-${useId()}`;
    const floatingId = `floating-${useId()}`;
    const ariaArgs = {
        floating: { id: floatingId, open },
        ref: { id: children.props.id || defaultRefId },
    };
    const refElAriaAttributes = (_a = buildRefElAriaAttributes === null || buildRefElAriaAttributes === void 0 ? void 0 : buildRefElAriaAttributes(ariaArgs)) !== null && _a !== void 0 ? _a : {};
    const floatingElAriaAttributes = (_b = buildFloatingElAriaAttributes === null || buildFloatingElAriaAttributes === void 0 ? void 0 : buildFloatingElAriaAttributes(ariaArgs)) !== null && _b !== void 0 ? _b : {};
    const { getReferenceProps, getFloatingProps } = useInteractions(context);
    if (!content) {
        return children;
    }
    const referenceProps = getReferenceProps();
    const baseTransitionClassname = `cmp-${path}`;
    const popover = (React.createElement(CSSTransition, { nodeRef: ref, in: open, timeout: { enter: 0, exit: 200 }, classNames: baseTransitionClassname, mountOnEnter: true, unmountOnExit: true, appear: true }, (state) => (React.createElement("div", Object.assign({ ref: ref }, getFloatingProps({
        ref: refs.setFloating,
        id: floatingId,
    }), { style: {
            position: strategy,
            top: y !== null && y !== void 0 ? y : 0,
            left: x !== null && x !== void 0 ? x : 0,
            zIndex: 1999,
        }, role: role }, floatingElAriaAttributes),
        React.createElement("div", { tabIndex: -1, "data-testid": 'floating-element-panel', className: clsx(className, `${baseTransitionClassname} ${baseTransitionClassname}--${state}`), ref: panelRef }, typeof content === 'function'
            ? content(Object.assign(Object.assign({}, referenceProps), { close: () => setOpen(false) }))
            : content),
        !hidePointer && (React.createElement("span", { id: `${floatingId}-pointer`, ref: pointerRef, className: clsx(`${baseTransitionClassname}-arrow`, `${baseTransitionClassname}--${state}`), style: getArrowSize(6, statefulPlacement, pointerX, pointerY) }))))));
    return (React.createElement(React.Fragment, null,
        React.cloneElement(children, Object.assign(Object.assign(Object.assign(Object.assign({ ref: triggerRef }, refElAriaAttributes), { id: floatingElAriaAttributes['aria-labelledby'] && !children.props.id
                ? defaultRefId
                : undefined }), referenceProps), { onClick: children.props.onClick || referenceProps.onClick, onKeyDown: children.props.onKeyDown || referenceProps.onKeyDown, onKeyUp: children.props.onKeyUp || referenceProps.onKeyUp, onPointerDown: children.props.onPointerDown || referenceProps.onPointerDown })),
        inline ? (popover) : (React.createElement(FloatingPortal, { root: root }, popover))));
}
const BaseFloatingElement = React.forwardRef(_BaseFloatingElement);
export default BaseFloatingElement;
//# sourceMappingURL=Base.js.map